import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  ChartLineUp,
  Info,
  CheckCircle,
  Buildings,
  CurrencyDollar,
  Gear,
  ShieldCheck,
  TrendUp,
  Sparkle
} from "@phosphor-icons/react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Layout } from "../../components/layout/Layout";
import { formatCurrency, API_URL } from "../../lib/utils";
import { LiquidGlassBg } from "../../components/ui/LiquidGlassComponents";

const models = [
  { value: "no_healthcare", label: "No Formal Healthcare" },
  { value: "reimbursement", label: "Reimbursement Based" },
  { value: "insurance", label: "Insurance / Takaful" },
  { value: "employer_pool", label: "Employer Funded Pool" },
  { value: "hybrid", label: "Hybrid Model" },
];

export default function AdvancedCalculator() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    number_of_employees: "",
    industry: "",
    annual_healthcare_spend: "",
    monthly_healthcare_spend: "",
    claims_per_month: "",
    current_model: "",
    uses_broker: false,
    uses_manual_reimbursement: true,
    has_employee_wallet: false,
    has_opd_pharmacy: false,
    has_preventive_wellness: false,
    annual_insurance_premium: "",
    annual_reimbursement_payout: "",
    annual_pool_funding: "",
    annual_opd_pharmacy_spend: "",
    monthly_admin_hours: "",
    monthly_admin_salary: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_URL}/calculators/advanced`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          number_of_employees: parseInt(formData.number_of_employees),
          industry: formData.industry || null,
          annual_healthcare_spend:
            parseFloat(formData.annual_healthcare_spend) || 0,
          monthly_healthcare_spend:
            parseFloat(formData.monthly_healthcare_spend) || null,
          claims_per_month: parseInt(formData.claims_per_month) || null,
          current_model: formData.current_model,
          uses_broker: formData.uses_broker,
          uses_manual_reimbursement: formData.uses_manual_reimbursement,
          has_employee_wallet: formData.has_employee_wallet,
          has_opd_pharmacy: formData.has_opd_pharmacy,
          has_preventive_wellness: formData.has_preventive_wellness,
          annual_insurance_premium:
            parseFloat(formData.annual_insurance_premium) || null,
          annual_reimbursement_payout:
            parseFloat(formData.annual_reimbursement_payout) || null,
          annual_pool_funding: parseFloat(formData.annual_pool_funding) || null,
          annual_opd_pharmacy_spend:
            parseFloat(formData.annual_opd_pharmacy_spend) || null,
          monthly_admin_hours: parseFloat(formData.monthly_admin_hours) || null,
          monthly_admin_salary:
            parseFloat(formData.monthly_admin_salary) || null,
        }),
      });

      if (!response.ok) throw new Error("Calculation failed");
      const data = await response.json();
      setResult(data);
      setStep(3);
    } catch (err) {
      setError("Failed to calculate. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <Layout>
      <section className="bg-[#030712] min-h-screen relative overflow-hidden pt-32 pb-20">
        <LiquidGlassBg />
        
        {/* Decorative elements */}
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#0048ff]/30 to-transparent" />

        <div className="container-wide relative z-10">
          <div className="max-w-6xl mx-auto">
            {/* Header */}
            <motion.div
              className="text-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-[#0048ff] text-sm font-medium mb-6 backdrop-blur-md">
                <ChartLineUp size={18} weight="bold" />
                <span>Deep Infrastructure Audit</span>
              </div>
              <h1 className="text-[40px] md:text-[64px] font-bold text-white mb-6 tracking-tighter leading-tight">
                Advanced <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0048ff] to-cyan-400">Optimization</span> Engine
              </h1>
              <p className="text-white/40 text-lg max-w-2xl mx-auto leading-relaxed">
                Connect your operational data points to architect a high-governance healthcare model tailored for scale.
              </p>
            </motion.div>

            {/* Progress Stepper */}
            <div className="flex justify-center mb-16 px-4">
              <div className="flex items-center w-full max-w-2xl">
                {[
                  { id: 1, label: "Profile", icon: Buildings },
                  { id: 2, label: "Financials", icon: CurrencyDollar },
                  { id: 3, label: "Analysis", icon: Gear },
                ].map((s, i) => (
                  <React.Fragment key={s.id}>
                    <div className="flex flex-col items-center relative group">
                      <div
                        className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 border-2 ${
                          step >= s.id
                            ? "bg-[#0048ff] border-[#0048ff] text-white shadow-[0_0_30px_rgba(0,72,255,0.4)]"
                            : "bg-white/5 border-white/10 text-white/20"
                        }`}
                      >
                        {step > s.id ? (
                          <CheckCircle size={28} weight="fill" />
                        ) : (
                          <s.icon size={24} weight="duotone" />
                        )}
                      </div>
                      <span className={`text-[10px] uppercase font-bold tracking-[0.2em] mt-3 ${step >= s.id ? "text-[#0048ff]" : "text-white/20"}`}>
                        {s.label}
                      </span>
                    </div>
                    {i < 2 && (
                      <div className="flex-1 h-px mx-4 mt-[-24px]">
                        <div className={`h-full transition-all duration-1000 ${step > s.id ? "bg-[#0048ff]" : "bg-white/10"}`} />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="max-w-4xl mx-auto">
              <AnimatePresence mode="wait">
                {/* Step 1: Company Profile */}
                {step === 1 && (
                  <motion.div
                    key="step1"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="liquid-glass bg-white/[0.03] backdrop-blur-2xl border border-white/10 rounded-[40px] p-8 md:p-12"
                  >
                    <div className="flex items-center gap-4 mb-10">
                      <div className="w-12 h-12 rounded-2xl bg-[#0048ff]/20 flex items-center justify-center text-[#0048ff] border border-[#0048ff]/30">
                        <Buildings size={24} weight="duotone" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white tracking-tight">Step 1: Company Profile</h2>
                        <p className="text-white/40 text-sm">Define your organizational architecture</p>
                      </div>
                    </div>

                    <div className="space-y-8">
                       <div className="grid md:grid-cols-2 gap-8">
                        <div className="space-y-3">
                          <Label className="text-white/60 text-sm font-medium ml-1">Number of Employees *</Label>
                          <Input
                            type="number"
                            placeholder="e.g., 500"
                            value={formData.number_of_employees}
                            onChange={(e) => updateField("number_of_employees", e.target.value)}
                            className="bg-white/5 border-white/10 text-white h-14 rounded-2xl focus:border-[#0048ff] transition-all"
                            required
                          />
                        </div>
                        <div className="space-y-3">
                          <Label className="text-white/60 text-sm font-medium ml-1">Industry</Label>
                          <Select value={formData.industry} onValueChange={(v) => updateField("industry", v)}>
                            <SelectTrigger className="bg-white/5 border-white/10 text-white h-14 rounded-2xl">
                              <SelectValue placeholder="Select industry" />
                            </SelectTrigger>
                            <SelectContent className="bg-[#0f111a] border-white/10 text-white">
                              {["Technology", "Manufacturing", "Healthcare", "Finance", "Retail", "Other"].map(ind => (
                                <SelectItem key={ind} value={ind.toLowerCase()} className="focus:bg-[#0048ff] focus:text-white cursor-pointer">
                                  {ind}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-8">
                        <div className="space-y-3">
                          <Label className="text-white/60 text-sm font-medium ml-1">Annual Spend (PKR) *</Label>
                          <Input
                            type="number"
                            placeholder="e.g., 40,000,000"
                            value={formData.annual_healthcare_spend}
                            onChange={(e) => updateField("annual_healthcare_spend", e.target.value)}
                            className="bg-white/5 border-white/10 text-white h-14 rounded-2xl focus:border-[#0048ff]"
                            required
                          />
                        </div>
                        <div className="space-y-3">
                          <Label className="text-white/60 text-sm font-medium ml-1">Avg Claims / Month</Label>
                          <Input
                            type="number"
                            placeholder="e.g., 50"
                            value={formData.claims_per_month}
                            onChange={(e) => updateField("claims_per_month", e.target.value)}
                            className="bg-white/5 border-white/10 text-white h-14 rounded-2xl focus:border-[#0048ff]"
                          />
                        </div>
                      </div>

                      <div className="space-y-3">
                        <Label className="text-white/60 text-sm font-medium ml-1">Current Healthcare Model *</Label>
                        <Select value={formData.current_model} onValueChange={(v) => updateField("current_model", v)}>
                          <SelectTrigger className="bg-white/5 border-white/10 text-white h-14 rounded-2xl">
                            <SelectValue placeholder="Select current model" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#0f111a] border-white/10 text-white">
                            {models.map((m) => (
                              <SelectItem key={m.value} value={m.value} className="focus:bg-[#0048ff] focus:text-white cursor-pointer">
                                {m.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Toggles Grid */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        {[
                          { id: "uses_broker", label: "Uses Insurance Broker?" },
                          { id: "uses_manual_reimbursement", label: "Manual Claims?" },
                          { id: "has_employee_wallet", label: "Employee Wallet?" },
                          { id: "has_opd_pharmacy", label: "OPD Coverage?" },
                          { id: "has_preventive_wellness", label: "Wellness Programs?" },
                        ].map(toggle => (
                          <div key={toggle.id} className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-2xl">
                            <Label className="text-sm font-medium text-white/60">{toggle.label}</Label>
                            <Switch 
                              checked={formData[toggle.id]} 
                              onCheckedChange={(v) => updateField(toggle.id, v)} 
                              className="data-[state=checked]:bg-[#0048ff]"
                            />
                          </div>
                        ))}
                      </div>

                      <Button 
                        onClick={() => setStep(2)} 
                        className="w-full h-16 bg-[#0048ff] hover:bg-[#003bce] text-white font-bold rounded-2xl tracking-wide group"
                        disabled={!formData.number_of_employees || !formData.annual_healthcare_spend || !formData.current_model}
                      >
                        <span className="flex items-center gap-2">
                          Configure Financial Parameters <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                        </span>
                      </Button>
                    </div>
                  </motion.div>
                )}

                {/* Step 2: Financial Details */}
                {step === 2 && (
                  <motion.div
                    key="step2"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    className="liquid-glass bg-white/[0.03] backdrop-blur-2xl border border-white/10 rounded-[40px] p-8 md:p-12"
                  >
                    <div className="flex items-center gap-4 mb-10">
                      <div className="w-12 h-12 rounded-2xl bg-[#0048ff]/20 flex items-center justify-center text-[#0048ff] border border-[#0048ff]/30">
                        <CurrencyDollar size={24} weight="duotone" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold text-white tracking-tight">Step 2: Financial Detail</h2>
                        <p className="text-white/40 text-sm">Deep dive into spend allocation</p>
                      </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-8">
                       <div className="grid md:grid-cols-2 gap-8">
                        <div className="space-y-3">
                          <Label className="text-white/60 text-sm font-medium ml-1">Insurance Premium (PKR)</Label>
                          <Input
                            type="number"
                            placeholder="Annual total"
                            value={formData.annual_insurance_premium}
                            onChange={(e) => updateField("annual_insurance_premium", e.target.value)}
                            className="bg-white/5 border-white/10 text-white h-14 rounded-2xl focus:border-[#0048ff]"
                          />
                        </div>
                        <div className="space-y-3">
                          <Label className="text-white/60 text-sm font-medium ml-1">Manual Payouts (PKR)</Label>
                          <Input
                            type="number"
                            placeholder="Annual total"
                            value={formData.annual_reimbursement_payout}
                            onChange={(e) => updateField("annual_reimbursement_payout", e.target.value)}
                            className="bg-white/5 border-white/10 text-white h-14 rounded-2xl focus:border-[#0048ff]"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-8">
                        <div className="space-y-3">
                          <Label className="text-white/60 text-sm font-medium ml-1">Pool Funding (PKR)</Label>
                          <Input
                            type="number"
                            placeholder="If self-funded"
                            value={formData.annual_pool_funding}
                            onChange={(e) => updateField("annual_pool_funding", e.target.value)}
                            className="bg-white/5 border-white/10 text-white h-14 rounded-2xl focus:border-[#0048ff]"
                          />
                        </div>
                        <div className="space-y-3">
                          <Label className="text-white/60 text-sm font-medium ml-1">OPD Spend (PKR)</Label>
                          <Input
                            type="number"
                            placeholder="Annual total"
                            value={formData.annual_opd_pharmacy_spend}
                            onChange={(e) => updateField("annual_opd_pharmacy_spend", e.target.value)}
                            className="bg-white/5 border-white/10 text-white h-14 rounded-2xl focus:border-[#0048ff]"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-8">
                        <div className="space-y-3">
                          <Label className="text-white/60 text-sm font-medium ml-1">Monthly Admin Hours</Label>
                          <Input
                            type="number"
                            placeholder="e.g., 40"
                            value={formData.monthly_admin_hours}
                            onChange={(e) => updateField("monthly_admin_hours", e.target.value)}
                            className="bg-white/5 border-white/10 text-white h-14 rounded-2xl focus:border-[#0048ff]"
                          />
                        </div>
                        <div className="space-y-3">
                          <Label className="text-white/60 text-sm font-medium ml-1">Avg Admin Salary (PKR)</Label>
                          <Input
                            type="number"
                            placeholder="Loaded monthly cost"
                            value={formData.monthly_admin_salary}
                            onChange={(e) => updateField("monthly_admin_salary", e.target.value)}
                            className="bg-white/5 border-white/10 text-white h-14 rounded-2xl focus:border-[#0048ff]"
                          />
                        </div>
                      </div>

                      {error && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400 text-sm">{error}</div>}

                      <div className="flex gap-4">
                        <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1 h-16 border-white/10 text-white hover:bg-white/5 rounded-2xl font-bold">
                          Back
                        </Button>
                        <Button type="submit" className="flex-[2] h-16 bg-[#0048ff] hover:bg-[#003bce] text-white font-bold rounded-2xl shadow-lg shadow-[#0048ff]/20" disabled={loading}>
                          {loading ? "Analyzing Infrastructure..." : "Execute Full Analysis"}
                        </Button>
                      </div>
                    </form>
                  </motion.div>
                )}

                {/* Step 3: Analysis Dashboard */}
                {step === 3 && result && (
                  <motion.div
                    key="step3"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="space-y-8"
                  >
                    {/* Primary Projection Card */}
                    <div className="liquid-glass bg-[#0048ff]/5 border border-[#0048ff]/20 rounded-[40px] p-8 md:p-12 relative overflow-hidden">
                       <div className="absolute top-0 right-0 w-80 h-80 bg-[#0048ff]/20 blur-[100px] rounded-full -translate-y-1/2 translate-x-1/2" />
                       
                       <div className="flex items-center gap-4 mb-8 relative z-10">
                        <div className="w-14 h-14 rounded-2xl bg-[#0048ff] flex items-center justify-center text-white shadow-lg shadow-[#0048ff]/40">
                          <TrendUp size={32} weight="bold" />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-white tracking-tight">Strategy Projection</h3>
                          <p className="text-white/40 text-sm">Targeted optimization for {formData.number_of_employees} employees</p>
                        </div>
                      </div>

                      <div className="grid lg:grid-cols-2 gap-12 relative z-10">
                        <div>
                          <p className="text-white/40 text-xs uppercase font-bold tracking-widest mb-3">Estimated Annual Efficiency Gain</p>
                          <div className="text-[48px] md:text-[64px] font-bold text-white tracking-tighter leading-none mb-4">
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0048ff] to-cyan-400">
                              {formatCurrency(result.total_estimated_savings.low)}
                            </span>
                            <span className="text-2xl text-white/20 mx-3">+</span>
                          </div>
                          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold">
                            <Sparkle size={14} weight="fill" />
                            <span>Per Employee: {formatCurrency(result.total_estimated_savings.per_employee_low)}</span>
                          </div>
                        </div>

                        <div className="space-y-6">
                           <h4 className="text-white font-bold text-lg">Top Recommended Rail</h4>
                           <div className="p-6 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-3xl">
                             <div className="text-[#0048ff] text-2xl font-black mb-2 uppercase tracking-tight">{result.recommendations[0]}</div>
                             <p className="text-white/40 text-sm leading-relaxed">Integrated architecture to eliminate governance leakage and automate administrative overhead.</p>
                           </div>
                        </div>
                      </div>
                    </div>

                    {/* Operational Breakdown */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="liquid-glass bg-white/[0.03] border border-white/10 rounded-[32px] p-8">
                        <h4 className="text-white font-bold mb-6 flex items-center gap-2">
                           <Gear size={20} className="text-[#0048ff]" />
                           Leakage Reduction
                        </h4>
                        <div className="space-y-4">
                           <div className="flex justify-between items-end">
                             <span className="text-white/40 text-sm">Optimization Rate</span>
                             <span className="text-white font-mono">{result.savings_breakdown.leakage_reduction?.rate || "15%"}</span>
                           </div>
                           <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                             <div className="h-full bg-[#0048ff] w-[65%]" />
                           </div>
                           <div className="text-2xl font-bold text-white">{formatCurrency(result.savings_breakdown.leakage_reduction?.low || 0)}</div>
                        </div>
                      </div>

                      <div className="liquid-glass bg-white/[0.03] border border-white/10 rounded-[32px] p-8">
                        <h4 className="text-white font-bold mb-6 flex items-center gap-2">
                           <ShieldCheck size={20} className="text-[#0048ff]" />
                           Governance Gain
                        </h4>
                        <div className="space-y-4">
                           <div className="flex justify-between items-end">
                             <span className="text-white/40 text-sm">Efficiency Rate</span>
                             <span className="text-white font-mono">{result.savings_breakdown.admin_efficiency?.rate || "20%"}</span>
                           </div>
                           <div className="h-2 w-full bg-white/5 rounded-full overflow-hidden">
                             <div className="h-full bg-cyan-400 w-[80%]" />
                           </div>
                           <div className="text-2xl font-bold text-white">{formatCurrency(result.savings_breakdown.admin_efficiency?.low || 0)}</div>
                        </div>
                      </div>
                    </div>

                    {/* Recommendations List */}
                    <div className="liquid-glass bg-white/[0.03] border border-white/10 rounded-[32px] p-8 md:p-10">
                      <h3 className="text-xl font-bold text-white mb-8">Architectural Roadmap</h3>
                      <div className="grid sm:grid-cols-2 gap-4">
                        {result.recommendations.map((rec, idx) => (
                          <div key={idx} className="group p-5 bg-white/5 border border-white/5 hover:border-[#0048ff]/30 rounded-2xl transition-all cursor-default flex items-start gap-4">
                            <div className="w-6 h-6 rounded-full bg-[#0048ff]/20 text-[#0048ff] flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-[#0048ff] group-hover:text-white transition-all">
                              <CheckCircle size={16} weight="bold" />
                            </div>
                            <span className="text-white/70 text-sm font-medium leading-tight group-hover:text-white transition-colors">{rec}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Final Actions */}
                    <div className="flex flex-col sm:flex-row gap-4 pt-4">
                      <Link to="/register" className="flex-[2]">
                        <Button className="w-full h-16 bg-[#0048ff] hover:bg-[#003bce] text-white font-bold rounded-2xl shadow-lg shadow-[#0048ff]/20 gap-3 group">
                          Implement Strategy <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                      <Link to="/demo" className="flex-1">
                        <Button variant="outline" className="w-full h-16 border-white/10 text-white hover:bg-white/5 rounded-2xl font-bold">
                          Technical Demo
                        </Button>
                      </Link>
                      <Button 
                        variant="ghost" 
                        onClick={() => { setStep(1); setResult(null); }}
                        className="flex-1 h-16 text-white/40 hover:text-white hover:bg-white/5 rounded-2xl font-bold"
                      >
                        Reset Engine
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
